import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, deleteDoc, doc, getDocs, query, where } from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import FiltroBar from "../../components/FiltroBar/FiltroBar";
import { useSortableTable, SortableHeader } from "../../hooks/useSortableTable";
import { formatData } from "../../utils/dateFormat";
import { db } from "../../config/firebase";

const COLLECTION_PROPOSTAS_PREVENTIVAS = "financeiro_propostas_preventivas";

function formatMoney(value) {
  const n = Number(value || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function FinanceiroPreventivasPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();
  const navigate = useNavigate();

  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [campoFiltro, setCampoFiltro] = useState("clienteNome");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [deletandoId, setDeletandoId] = useState(null);

  useEffect(() => {
    const carregar = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      setErro("");
      try {
        const nivel = nivelUsuario(usuario);
        const restringirEmpresa = !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;

        let qBase = query(
          collection(db, COLLECTION_PROPOSTAS_PREVENTIVAS),
          where("grupoId", "==", usuario.grupoId)
        );

        if (restringirEmpresa) {
          const empresaId = empresaAtual?.id || usuario?.empresaId || null;
          if (empresaId) {
            qBase = query(
              collection(db, COLLECTION_PROPOSTAS_PREVENTIVAS),
              where("grupoId", "==", usuario.grupoId),
              where("clienteId", "==", empresaId)
            );
          }
        }

        const snap = await getDocs(qBase);
        const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRegistros(lista);
      } catch (e) {
        console.error("Erro ao carregar propostas de preventivas:", e);
        setErro("Erro ao carregar propostas de preventivas.");
        setRegistros([]);
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, [usuario?.grupoId, usuario?.empresaId, empresaAtual?.id, modoGlobal]);

  const camposFiltro = [
    { value: "clienteNome", label: "Cliente" },
    { value: "academiaNome", label: "Academia" },
    { value: "tipoViagem", label: "Tipo viagem" },
    { value: "previsaoDias", label: "Previsao dias" },
  ];

  const filtrados = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return registros;

    return registros.filter((r) => {
      const base = String(r?.[campoFiltro] ?? "").toLowerCase();
      return base.includes(texto);
    });
  }, [registros, campoFiltro, textoFiltro]);

  const { sortedData, requestSort, getSortDirection } = useSortableTable(
    filtrados,
    "clienteNome"
  );

  const handleExcluir = async (registro) => {
    if (!registro?.id) return;
    const confirmar = window.confirm("Deseja excluir esta proposta de preventiva?");
    if (!confirmar) return;

    setDeletandoId(registro.id);
    setErro("");
    try {
      await deleteDoc(doc(db, COLLECTION_PROPOSTAS_PREVENTIVAS, registro.id));
      setRegistros((prev) => prev.filter((item) => item.id !== registro.id));
    } catch (e) {
      console.error("Erro ao excluir proposta:", e);
      setErro("Erro ao excluir proposta de preventiva.");
    } finally {
      setDeletandoId(null);
    }
  };

  return (
    <LayoutDesktop tema={tema} titulo="Propostas" subtitulo="Financeiro / Propostas / Preventivas">
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Preventivas</h3>
            <p>Propostas de preventiva com escopo e orcamento.</p>
          </div>
          <div className="pc-table-header-actions">
            <button
              type="button"
              className="pc-btn-primario"
              onClick={() => navigate("/financeiro/preventivas/nova")}
            >
              + Nova preventiva
            </button>
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          placeholder="Filtrar preventivas..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div style={{ background: "#7f1d1d", color: "#fecaca", padding: 8, borderRadius: 8, marginBottom: 12, fontSize: 14 }}>
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando propostas...</p>
          ) : sortedData.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhuma proposta de preventiva encontrada.</p>
          ) : (
            <table className="pc-table pc-table-expandable">
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  <SortableHeader
                    label="Cliente"
                    sortKey="clienteNome"
                    onSort={requestSort}
                    sortDirection={getSortDirection("clienteNome")}
                  />
                  <SortableHeader
                    label="Academia"
                    sortKey="academiaNome"
                    onSort={requestSort}
                    sortDirection={getSortDirection("academiaNome")}
                  />
                  <SortableHeader
                    label="Tipo viagem"
                    sortKey="tipoViagem"
                    onSort={requestSort}
                    sortDirection={getSortDirection("tipoViagem")}
                  />
                  <th>Equipamentos</th>
                  <th>Previsao (dias)</th>
                  <th>Valor total (R$)</th>
                  <th style={{ width: 170 }}>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((r) => {
                  const aberto = expandedId === r.id;
                  return (
                    <React.Fragment key={r.id}>
                      <tr className={aberto ? "pc-row-open" : undefined}>
                        <td>
                          <button
                            type="button"
                            className="pc-expand-btn"
                            aria-label={aberto ? "Recolher" : "Expandir"}
                            onClick={() => setExpandedId((prev) => (prev === r.id ? null : r.id))}
                          >
                            <span className={aberto ? "pc-expand-chevron open" : "pc-expand-chevron"}>▸</span>
                          </button>
                        </td>
                        <td>{r.clienteNome || "-"}</td>
                        <td>{r.academiaNome || "-"}</td>
                        <td>{r.tipoViagem || "-"}</td>
                        <td>{r.equipamentosTotal ?? 0}</td>
                        <td>{r.previsaoDias ?? "-"}</td>
                        <td>{formatMoney(r.valorTotal || 0)}</td>
                        <td>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button
                              type="button"
                              className="pc-btn-secundario"
                              onClick={() => navigate(`/financeiro/preventivas/editar/${r.id}`)}
                              disabled={deletandoId === r.id}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="pc-btn-secundario"
                              onClick={() => handleExcluir(r)}
                              disabled={deletandoId === r.id}
                            >
                              {deletandoId === r.id ? "Excluindo..." : "Excluir"}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {aberto && (
                        <tr className="pc-expanded-row">
                          <td colSpan={8}>
                            <div className="pc-expand-panel">
                              <div className="pc-expand-grid">
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Cliente</div>
                                  <div className="pc-expand-value">{r.clienteNome || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Academia</div>
                                  <div className="pc-expand-value">{r.academiaNome || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Endereco destino</div>
                                  <div className="pc-expand-value">{r.enderecoDestino || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Data criacao</div>
                                  <div className="pc-expand-value">{formatData(r.createdAt) || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Tipo de viagem</div>
                                  <div className="pc-expand-value">{r.tipoViagem || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Previsao de dias</div>
                                  <div className="pc-expand-value">{r.previsaoDias ?? "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Ajudantes</div>
                                  <div className="pc-expand-value">{r.quantidadeAjudantes ?? 0}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Gastos almoco (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.gastosAlmoco || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Km rodados</div>
                                  <div className="pc-expand-value">{r.kmRodados ?? 0}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Pedagio (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.valorPedagio || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Deslocamento (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.valorDeslocamento || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Mao de obra (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.valorMaoObra || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Pecas (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.valorPecas || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Desconto (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.valorDesconto || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Valor total (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.valorTotal || 0)}</div>
                                </div>
                                <div className="pc-expand-item" style={{ gridColumn: "1 / -1" }}>
                                  <div className="pc-expand-label">Equipamentos por tipo</div>
                                  <div className="pc-expand-value">
                                    {Object.entries(r.equipamentosPorTipo || {})
                                      .filter(([, qtd]) => Number(qtd || 0) > 0)
                                      .map(([tipo, qtd]) => `${tipo}: ${qtd}`)
                                      .join(" • ") || "-"}
                                  </div>
                                </div>
                                <div className="pc-expand-item" style={{ gridColumn: "1 / -1" }}>
                                  <div className="pc-expand-label">Observacoes</div>
                                  <div className="pc-expand-value">{r.observacoes || "-"}</div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}
